import {
  Accordion,
  AccordionProps,
  Badge,
  Box,
  createStyles,
  CSSObject,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  TabsValue,
  Text,
} from "@mantine/core";
import { Prism } from "@mantine/prism";
import { Button, tokens } from "@blurb/codex-react";
import React from "react";
import { optObj } from "../../utils/misc.util";

const color = tokens.color.semantic;

/** Below this width the two-column field grid collapses to one column. */
const TWO_COLUMN_MIN_WIDTH = 1256;

/** Figma "Accordion Fill - Examples": rows sit 16px apart, each on a subtle border with no shadow —
 *  the shadow in the design belongs to the card these rows sit inside. */
const useAccordionStyles = createStyles(() => ({
  control: {
    "&:hover": {
      background: "transparent",
    },
  },

  panel: {
    borderTop: "1px solid",
    borderColor: color.border.faint,
  },

  item: {
    borderBottom: 0,
    overflow: "hidden",
    border: `1px solid ${color.border.subtle}`,
    borderRadius: tokens.radius.sm,
    marginBottom: 16,
    backgroundColor: color.bg.graySubtler,
  },
}));

/**
 * The result chrome: Figma "Tab Pill H" (40px tall, 16px padding, Body M label, active tab filled
 * brand-subtlest) above a bordered panel.
 *
 * @mantine/prism hardcodes the classNames of both the tabs and the Prism inside each panel, which
 * shuts off their styles APIs, so these are matched on their static classes instead.
 */
const useResultStyles = createStyles(() => ({
  results: {
    ".mantine-Tabs-tab": {
      height: 40,
      paddingLeft: 16,
      paddingRight: 16,
      fontSize: 16,
      color: color.text.bold,
      borderColor: color.border.subtle,
      "&[data-active]": {
        backgroundColor: color.bg.primarySubtlest,
        color: color.text.linkActive,
        fontWeight: 700,
      },
    },

    ".mantine-Prism-root": {
      borderColor: color.border.subtle,
    },
  },
}));

// Mantine's own defaults — a right-aligned 24px chevron that rotates on expand — are what the design
// asks for, so no chevron is passed here.
export const ApiAccordion: React.FC<AccordionProps> = (props) => {
  const { classes } = useAccordionStyles();
  return <Accordion classNames={classes} {...props} />;
};

export enum ApiMethod {
  POST = "POST",
  DELETE = "DELETE",
  GET = "GET",
  PUT = "PUT",
}

/** Figma "Badge / Square": a fixed width so GET and POST paths line up down the list. */
const badgeShape: CSSObject = {
  width: 80,
  height: 28,
  color: color.text.inverse,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  textTransform: "none",
};

// Built once per method rather than per render: the fills come from the design system's accent
// tokens, not Mantine's palette, whose `green` is a teal.
const methodBadgeSx: { [key in ApiMethod]: CSSObject } = {
  [ApiMethod.POST]: { ...badgeShape, backgroundColor: color.bg.accentGreen },
  [ApiMethod.DELETE]: { ...badgeShape, backgroundColor: color.bg.accentRed },
  [ApiMethod.GET]: { ...badgeShape, backgroundColor: color.bg.accentBlue },
  [ApiMethod.PUT]: { ...badgeShape, backgroundColor: color.bg.accentOrange },
};

const pathSx: CSSObject = { color: color.text.bold };
const descriptionSx: CSSObject = { color: color.text.inverseSubtle };

export interface ApiAccordionLabelProps {
  method: ApiMethod;
  path: string;
  description: string;
}

export const ApiAccordionLabel: React.FC<ApiAccordionLabelProps> = ({ method, path, description }) => {
  return (
    <Group spacing={6}>
      <Badge radius="sm" variant="filled" sx={methodBadgeSx[method]} children={method} />
      <Text sx={pathSx} children={path} />
      <Text sx={descriptionSx} children={description} />
    </Group>
  );
};

/** All the action button needs. A wider type would couple every call site to a button library. */
interface ApiAccordionFormButtonProps {
  loading: boolean;
  disabled: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

interface ApiAccordionFormProps {
  curl: string;
  response: { data?: any; error?: any };
  children: React.ReactNode;
  buttonProps: ApiAccordionFormButtonProps;
  jsonInput?: React.ReactNode;
}

export const ApiAccordionForm: React.FC<ApiAccordionFormProps> = ({
  curl,
  response,
  children,
  buttonProps,
  jsonInput,
}) => {
  const { classes } = useResultStyles();
  const [tab, setTab] = React.useState<TabsValue>("curl");

  React.useEffect(() => {
    if (response.error) {
      setTab("error");
    } else if (response.data) {
      setTab("response");
    }
  }, [response]);

  return (
    <Stack>
      {/* Figma "Fields": 16px between columns, 8px between rows. */}
      <SimpleGrid
        spacing={16}
        verticalSpacing={8}
        breakpoints={[
          { minWidth: TWO_COLUMN_MIN_WIDTH, cols: 2 },
          { maxWidth: TWO_COLUMN_MIN_WIDTH, cols: 1 },
        ]}
        children={children}
      />

      {jsonInput}

      <Group>
        <Button variant="filled" intent="primary" size="small" {...buttonProps} children="Submit" />
      </Group>

      <Divider />

      <Box className={classes.results} sx={{ position: "relative", display: "flex" }}>
        <Box sx={{ flex: 1, width: 0 }}>
          <Prism.Tabs value={tab} onTabChange={setTab}>
            <Prism.TabsList>
              <Prism.Tab value="curl" children="Curl" />
              <Prism.Tab value="response" children="Response" />
              <Prism.Tab
                value="error"
                style={optObj(response.error, {
                  color: color.text.danger,
                  backgroundColor: color.bg.dangerSubtlest,
                  borderColor: color.border.dangerSubtlest,
                })}
                children="Error"
              />
            </Prism.TabsList>

            <Prism.Panel value="curl" language="bash" children={curl} />
            <Prism.Panel value="response" language="json" children={JSON.stringify(response.data || {}, null, 2)} />
            <Prism.Panel value="error" language="json" children={JSON.stringify(response.error || {}, null, 2)} />
          </Prism.Tabs>
        </Box>
      </Box>
    </Stack>
  );
};
